// Life & Wealth Solutions - Main Dashboard
// Phoenix Rescue Apps - Overview Dashboard
//
// FIXED: This component previously pointed at a dead API URL
// (http://localhost:5000/api — port 5000, no /v1 — the legacy Node
// server's port, which is not what runs in production) and silently
// fell back to hardcoded fake numbers (247 leads, $127,500 revenue,
// 18.5% conversion) in its catch block with zero indication to the
// user that the numbers were fake. Since this was the FIRST page every
// user saw after login, it served fabricated stats 100% of the time.
//
// Also: "Total Leads" and "Active Deals" had no real backing anywhere —
// there is no leads or deals table in the actual database schema. Those
// two cards have been replaced with metrics that ARE real today
// (Policies Bound, Pending Approvals). A real leads/deals pipeline is a
// separate, dedicated backend build, not something to fake here.

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../hooks/useAuth';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

interface DashboardStats {
  policiesBound: number;
  policyGrowth: number;
  pendingApprovals: number;
  monthlyRevenue: number;
  revenueGrowth: number;
  automationRate: number;
}

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('access_token');
      const agencyId = user?.agency_id;
      if (!agencyId) throw new Error('No agency associated with this account');

      const [analyticsRes, approvalsRes] = await Promise.all([
        axios.get(`${API_URL}/analytics/dashboard`, {
          params: { agency_id: agencyId, range: '30d' },
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/agent12/pending-approvals/${agencyId}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      const m = analyticsRes.data.efficiencyMetrics;
      setStats({
        policiesBound: m.policiesBound,
        policyGrowth: m.policyGrowth,
        pendingApprovals: approvalsRes.data.total ?? 0,
        monthlyRevenue: m.totalRevenue,
        revenueGrowth: m.revenueGrowth,
        automationRate: m.automationRate,
      });
    } catch (err: any) {
      console.error('Failed to fetch dashboard data:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load dashboard data');
      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, change, icon, color, format }: any) => (
    <div className="bg-white rounded-lg shadow p-6 border border-gray-200 hover:shadow-lg transition-shadow">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="mt-2 text-3xl font-bold text-gray-900">
            {format === 'currency' && '$'}
            {format === 'currency' ? value.toLocaleString() : value}
            {format === 'percentage' && '%'}
          </p>
          {change !== undefined && (
            <div className="mt-2 flex items-center">
              <span className={`text-sm font-medium ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {change >= 0 ? '↑' : '↓'} {Math.abs(change)}%
              </span>
              <span className="ml-2 text-sm text-gray-500">vs prior period</span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-lg ${color}`}>{icon}</div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-500 mt-1">Welcome back! Here's what's happening today.</p>
        </div>
        <div className="flex space-x-3">
          <button className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            <span className="text-sm font-medium">Export</span>
          </button>
          <Link to="/agent12" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
            <span className="text-sm font-medium">Agent 12 Dashboard</span>
          </Link>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error} — showing no data rather than estimated figures.
        </div>
      )}

      {!error && stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Policies Bound (30d)"
            value={stats.policiesBound}
            change={stats.policyGrowth}
            color="bg-blue-50"
            icon={
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
          <StatCard
            title="Pending Approvals"
            value={stats.pendingApprovals}
            color="bg-green-50"
            icon={
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
          <StatCard
            title="Monthly Revenue"
            value={stats.monthlyRevenue}
            change={stats.revenueGrowth}
            format="currency"
            color="bg-purple-50"
            icon={
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
          <StatCard
            title="Automation Rate"
            value={stats.automationRate}
            format="percentage"
            color="bg-orange-50"
            icon={
              <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            }
          />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Quick Actions</h2>
          <div className="space-y-3">
            <Link to="/agent12" className="flex items-center p-3 rounded-lg hover:bg-gray-50 transition-colors border border-gray-200">
              <div className="p-2 bg-blue-50 rounded-lg">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" />
                </svg>
              </div>
              <span className="ml-3 text-sm font-medium text-gray-900">Review Pending Approvals</span>
            </Link>
            <Link to="/analytics" className="flex items-center p-3 rounded-lg hover:bg-gray-50 transition-colors border border-gray-200">
              <div className="p-2 bg-green-50 rounded-lg">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <span className="ml-3 text-sm font-medium text-gray-900">View Analytics</span>
            </Link>
            <Link to="/compliance" className="flex items-center p-3 rounded-lg hover:bg-gray-50 transition-colors border border-gray-200">
              <div className="p-2 bg-purple-50 rounded-lg">
                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <span className="ml-3 text-sm font-medium text-gray-900">Compliance Dashboard</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
